import React, {
  useState,
  useCallback,
  useEffect,
} from "react";

import PropTypes from "prop-types";
import clsx from "clsx";

import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  TextField,
  makeStyles,
  MenuItem,
  Grid,
  Backdrop,
  CircularProgress,
  Typography,
} from "@material-ui/core";

import { useAlert } from "react-alert";

import {
  updateExhibit,
  getFindById,
  uploadCover,
} from "../../../adminModel/exihibition";

import { useParams } from "react-router-dom";

// =====================================================
// STYLES
// =====================================================

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
  },

  card: {
    border: "1px solid #e5e5e5",
    borderRadius: 8,
    boxShadow: "0 2px 6px rgba(0,0,0,0.04)",
    overflow: "hidden",
  },

  cardHeader: {
    padding: theme.spacing(2.5, 3),
    backgroundColor: "#fafafa",
  },

  cardTitle: {
    fontSize: 17,
    fontWeight: 600,
    color: "#222",
  },

  cardSubtitle: {
    marginTop: 4,
    fontSize: 12,
    color: "#888",
  },

  cardContent: {
    padding: theme.spacing(3),
  },

  section: {
    width: "100%",
  },

  sectionHeader: {
    marginBottom: theme.spacing(2),
    paddingBottom: theme.spacing(1),
    borderBottom: "1px solid #eeeeee",
  },

  sectionTitle: {
    fontSize: 13,
    fontWeight: 600,
    color: "#333",
    textTransform: "uppercase",
    letterSpacing: "0.4px",
  },

  sectionDescription: {
    marginTop: 3,
    fontSize: 11,
    color: "#999",
  },

  field: {
    marginBottom: theme.spacing(2),

    "& .MuiOutlinedInput-root": {
      backgroundColor: "#fff",
      borderRadius: 6,
    },

    "& .MuiInputBase-input": {
      fontSize: 13,
    },

    "& .MuiInputLabel-root": {
      fontSize: 13,
    },
  },

  coverCard: {
    marginTop: theme.spacing(3),
    border: "1px solid #e5e5e5",
    borderRadius: 8,
    boxShadow: "0 2px 6px rgba(0,0,0,0.04)",
    overflow: "hidden",
  },

  coverPreview: {
    width: "100%",
    height: 215,
    border: "1px solid #e5e5e5",
    borderRadius: 6,
    backgroundColor: "#fff",
    backgroundSize: "contain",
    backgroundPosition: "center",
    backgroundRepeat: "no-repeat",
  },

  actionWrapper: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    padding: theme.spacing(1.5, 3),
    backgroundColor: "#fafafa",
  },

  actionButton: {
    minWidth: 140,
    height: 40,
    borderRadius: 6,
    textTransform: "none",
    fontSize: 13,
    fontWeight: 600,
    boxShadow: "none",

    "&:hover": {
      boxShadow: "none",
    },
  },

  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: "#fff",
  },
}));

// =====================================================
// COMPONENT
// =====================================================

const Create = ({ className, ...rest }) => {
  const classes = useStyles();
  const alert = useAlert();

  const { id } = useParams();

  const [loading, setLoading] = useState(false);

  // =========================
  // FORM VALUES
  // =========================

  const [values, setValues] = useState({
    exhibit_name: "",
    exhibit_desc: "",
    date_start: "",
    date_end: "",
    isVisible: "",
    auction_status: "",
  });

  // =========================
  // COVER
  // =========================

  const [cover, setCover] = useState("");
  const [cover1, setCover1] = useState("");

  // =========================
  // FORM CHANGE
  // =========================

  const handleChange = (e) => {
    if (e.target.name === "auction_cover") {
      setCover1(e.target.files[0]);
      setCover(e.target.files[0]);
    } else {
      setValues({
        ...values,
        [e.target.name]: e.target.value,
      });
    }
  };

  // =====================================================
  // UPDATE EXHIBITION
  // =====================================================

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    setLoading(true);

    const arrayData = {
      id: id,
      exhibit_name: values.exhibit_name,
      exhibit_desc: values.exhibit_desc,
      date_start: values.date_start,
      date_end: values.date_end,
      isVisible: values.visible,
      auction_status: values.auction_status,
    };

    try {
      const result =
        await updateExhibit(arrayData);

      setLoading(false);

      if (
        result ===
        "Auction Name already Exist"
      ) {
        alert.error(result);
      } else {
        alert.success(result);
      }
    } catch (e) {
      setLoading(false);
      console.log(e);
    }
  };

  // =====================================================
  // FETCH EXHIBITION
  // =====================================================

  const FetchAuction = useCallback(
    async () => {
      setLoading(true);

      async function getAuction() {
        const response =
          await getFindById(id);

        setLoading(false);

        let results = {};

        response.map((val) => {
          return (results = val);
        });

        setCover(
          results.exhibit_cover
        );

        setValues({
          exhibit_name:
            results.exhibit_name,

          exhibit_desc:
            results.exhibit_desc,

          date_start:
            results.date_start,

          date_end:
            results.date_end,

          isVisible:
            results.isVisible,

          auction_status:
            results.auction_status,
        });
      }

      getAuction().catch(console.error);
    },
    [id]
  );

  useEffect(() => {
    FetchAuction();
  }, [FetchAuction]);

  // =====================================================
  // UPLOAD COVER
  // =====================================================

  const handleUploadCoverFormSubmit = (
    e
  ) => {
    e.preventDefault();

    let formData = new FormData();

    formData.append(
      "files",
      cover
    );

    formData.append(
      "id",
      id
    );

    setLoading(true);

    async function fetchData() {
      const results =
        await uploadCover(
          formData
        );

      setLoading(false);

      alert.success(results);
    }

    fetchData().catch(console.error);
  };

  // =====================================================
  // RENDER
  // =====================================================

  return (
    <div
      className={clsx(
        classes.root,
        className
      )}
      {...rest}
    >
      {/* =========================
          MAIN FORM
      ========================= */}

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <form
            onSubmit={
              handleFormSubmit
            }
            autoComplete="off"
          >
            <Card
              className={
                classes.card
              }
            >
              {/* HEADER */}

              <CardHeader
                className={
                  classes.cardHeader
                }
                title={
                  <Typography
                    className={
                      classes.cardTitle
                    }
                  >
                    Edit Exhibition
                  </Typography>
                }
                subheader={
                  <Typography
                    className={
                      classes.cardSubtitle
                    }
                  >
                    Update exhibition
                    information and settings
                  </Typography>
                }
              />

              <Divider />

              {/* CONTENT */}

              <CardContent
                className={
                  classes.cardContent
                }
              >
                <div
                  className={
                    classes.section
                  }
                >
                  <div
                    className={
                      classes.sectionHeader
                    }
                  >
                    <Typography
                      className={
                        classes.sectionTitle
                      }
                    >
                      Exhibition Information
                    </Typography>

                    <Typography
                      className={
                        classes.sectionDescription
                      }
                    >
                      Manage the exhibition
                      details, schedule and
                      visibility.
                    </Typography>
                  </div>

                  <Grid
                    container
                    spacing={3}
                  >
                    {/* EXHIBITION NAME */}

                    <Grid
                      item
                      md={6}
                      xs={12}
                    >
                      <TextField
                        className={
                          classes.field
                        }
                        fullWidth
                        label="Exhibition Name"
                        name="exhibit_name"
                        value={
                          values.exhibit_name
                        }
                        type="text"
                        variant="outlined"
                        onChange={
                          handleChange
                        }
                        InputLabelProps={{
                          shrink: true,
                        }}
                      />
                    </Grid>

                    {/* DESCRIPTION */}

                    <Grid
                      item
                      md={6}
                      xs={12}
                    >
                      <TextField
                        className={
                          classes.field
                        }
                        fullWidth
                        multiline
                        minRows={3}
                        label="Exhibition Description"
                        name="exhibit_desc"
                        value={
                          values.exhibit_desc
                        }
                        type="text"
                        variant="outlined"
                        onChange={
                          handleChange
                        }
                        InputLabelProps={{
                          shrink: true,
                        }}
                      />
                    </Grid>

                    {/* START DATE */}

                    <Grid
                      item
                      md={6}
                      xs={12}
                    >
                      <TextField
                        className={
                          classes.field
                        }
                        fullWidth
                        label="Start Date"
                        name="date_start"
                        type="date"
                        value={
                          values.date_start
                            ? values.date_start.slice(
                                0,
                                10
                              )
                            : ""
                        }
                        onChange={
                          handleChange
                        }
                        InputLabelProps={{
                          shrink: true,
                        }}
                        variant="outlined"
                      />
                    </Grid>

                    {/* END DATE */}

                    <Grid
                      item
                      md={6}
                      xs={12}
                    >
                      <TextField
                        className={
                          classes.field
                        }
                        fullWidth
                        label="End Date"
                        name="date_end"
                        type="date"
                        value={
                          values.date_end
                            ? values.date_end.slice(
                                0,
                                10
                              )
                            : ""
                        }
                        onChange={
                          handleChange
                        }
                        InputLabelProps={{
                          shrink: true,
                        }}
                        variant="outlined"
                      />
                    </Grid>

                    {/* VISIBILITY */}

                    <Grid
                      item
                      md={6}
                      xs={12}
                    >
                      <TextField
                        className={
                          classes.field
                        }
                        fullWidth
                        select
                        label="Visibility Status"
                        name="visible"
                        value={
                          values.isVisible
                        }
                        onChange={
                          handleChange
                        }
                        variant="outlined"
                      >
                        <MenuItem value={1}>
                          Show
                        </MenuItem>

                        <MenuItem value={0}>
                          Unshow
                        </MenuItem>
                      </TextField>
                    </Grid>

                    {/* AUCTION STATUS */}

                    <Grid
                      item
                      md={6}
                      xs={12}
                    >
                      <TextField
                        className={
                          classes.field
                        }
                        fullWidth
                        select
                        name="auction_status"
                        label="Exhibition Status"
                        value={
                          values.auction_status
                        }
                        onChange={
                          handleChange
                        }
                        variant="outlined"
                      >
                        <MenuItem value={0}>
                          PREVIOUS AUCTION
                        </MenuItem>

                        <MenuItem value={1}>
                          UPCOMING AUCTION
                        </MenuItem>

                        <MenuItem value={2}>
                          ONGOING AUCTION
                        </MenuItem>
                      </TextField>
                    </Grid>
                  </Grid>
                </div>
              </CardContent>

              <Divider />

              {/* ACTION */}

              <Box
                className={
                  classes.actionWrapper
                }
              >
                <Button
                  className={
                    classes.actionButton
                  }
                  color="primary"
                  variant="contained"
                  type="submit"
                  disabled={loading}
                >
                  {loading
                    ? "Updating..."
                    : "Update Details"}
                </Button>
              </Box>
            </Card>
          </form>
        </Grid>
      </Grid>

      {/* =========================
          COVER SECTION
      ========================= */}

      <Grid
        container
        spacing={3}
        style={{
          marginTop: 8,
        }}
      >
        {/* COVER PREVIEW */}

        <Grid
          item
          lg={4}
          md={5}
          xs={12}
        >
          <Card
            className={
              classes.coverCard
            }
          >
            <CardHeader
              className={
                classes.cardHeader
              }
              title={
                <Typography
                  className={
                    classes.cardTitle
                  }
                >
                  Exhibition Cover
                </Typography>
              }
              subheader={
                <Typography
                  className={
                    classes.cardSubtitle
                  }
                >
                  Current exhibition
                  cover
                </Typography>
              }
            />

            <Divider />

            <CardContent
              className={
                classes.cardContent
              }
            >
              <div
                className={
                  classes.coverPreview
                }
                style={{
                  backgroundImage: `url(${
                    cover1 === ""
                      ? cover
                        ? cover
                        : "/static/blank.jpeg"
                      : URL.createObjectURL(
                          cover1
                        )
                  })`,
                }}
              />
            </CardContent>
          </Card>
        </Grid>

        {/* UPLOAD COVER */}

        <Grid
          item
          lg={8}
          md={7}
          xs={12}
        >
          <form
            onSubmit={
              handleUploadCoverFormSubmit
            }
          >
            <Card
              className={
                classes.coverCard
              }
            >
              <CardHeader
                className={
                  classes.cardHeader
                }
                title={
                  <Typography
                    className={
                      classes.cardTitle
                    }
                  >
                    Update Exhibition Cover
                  </Typography>
                }
                subheader={
                  <Typography
                    className={
                      classes.cardSubtitle
                    }
                  >
                    Select a new cover image
                    to replace the current one
                  </Typography>
                }
              />

              <Divider />

              <CardContent
                className={
                  classes.cardContent
                }
              >
                <TextField
                  className={
                    classes.field
                  }
                  fullWidth
                  label="Select Cover Image"
                  name="auction_cover"
                  InputLabelProps={{
                    shrink: true,
                  }}
                  type="file"
                  variant="outlined"
                  onChange={
                    handleChange
                  }
                />
              </CardContent>

              <Divider />

              <Box
                className={
                  classes.actionWrapper
                }
              >
                <Button
                  className={
                    classes.actionButton
                  }
                  color="primary"
                  variant="contained"
                  type="submit"
                  disabled={loading}
                >
                  {loading
                    ? "Uploading..."
                    : "Upload Cover"}
                </Button>
              </Box>
            </Card>
          </form>
        </Grid>
      </Grid>

      {/* =========================
          LOADING
      ========================= */}

      <Backdrop
        className={
          classes.backdrop
        }
        open={loading}
      >
        <CircularProgress
          color="inherit"
        />
      </Backdrop>
    </div>
  );
};

Create.propTypes = {
  className:
    PropTypes.string,
};

export default Create;